import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Users, Check } from "lucide-react";

const POSITIONS = ["bartender","server","kitchen","host","security","manager","cashier","runner","promoter","other"];
const PAY_TYPES = ["hourly", "salary", "per_shift"];

export default function EmployeesPage() {
  const { branchId, hasPerm } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data } = await api.get("/employees", { params });
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load employees");
    }
  };
  useEffect(() => { load(); }, [branchId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = {
          name: form.name, position: form.position,
          pay_type: form.pay_type, pay_rate: parseFloat(form.pay_rate) || 0,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone, notes: form.notes,
          bank_name: form.bank_name || "",
          bank_account_number: form.bank_account_number || "",
          bank_account_name: form.bank_account_name || "",
        };
        await api.patch(`/employees/${editing.id}`, patch);
        toast.success("Employee updated");
      } else {
        if (!branchId) { toast.error("Select a branch first"); return; }
        await api.post("/employees", {
          ...form,
          branch_id: branchId,
          contact_email: form.contact_email || null,
          pay_rate: parseFloat(form.pay_rate) || 0,
        });
        toast.success("Employee added");
      }
      setCreating(false); setEditing(null); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      code: e.code, name: e.name, position: e.position,
      pay_type: e.pay_type, pay_rate: e.pay_rate,
      contact_email: e.contact_email || "", contact_phone: e.contact_phone || "",
      notes: e.notes || "",
      bank_name: e.bank_name || "",
      bank_account_number: e.bank_account_number || "",
      bank_account_name: e.bank_account_name || "",
    });
    setCreating(true);
  };

  const toggle = async (e) => {
    try {
      await api.patch(`/employees/${e.id}`, { active: !e.active });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let out = employees;
    if (filter !== "all") out = out.filter((e) => e.position === filter);
    if (search) {
      const s = search.toLowerCase();
      out = out.filter((e) => e.name.toLowerCase().includes(s) || e.code.toLowerCase().includes(s));
    }
    return out;
  }, [employees, filter, search]);

  const positions = Array.from(new Set(employees.map((e) => e.position)));

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="employees-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">HR · Master file</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Employees</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every person who works your bar — including the ones who never touch a login. Codes drive attendance scans; pay type &amp; rate feed the timesheet.
          </p>
        </div>
        {hasPerm("payroll.view") && (
          <button onClick={() => { setEditing(null); setForm(BLANK); setCreating(true); }}
                  data-testid="emp-new-btn" className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> Add employee
          </button>
        )}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3 animate-fade-up animate-fade-up-1">
        <input placeholder="Search name or code…" value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="emp-search"
               className="h-10 rounded-md border hairline surface-card px-3 text-sm ink w-72"/>
        <div className="flex items-center gap-1 border hairline-soft rounded-full p-1 flex-wrap">
          <button onClick={() => setFilter("all")}
                  data-testid="emp-filter-all"
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold transition-all ${
                    filter === "all" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                  }`}>All <span className="mono opacity-70">{employees.length}</span></button>
          {positions.map((p) => (
            <button key={p} onClick={() => setFilter(p)}
                    data-testid={`emp-filter-${p}`}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold transition-all ${
                      filter === p ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                    }`}>{p} <span className="mono opacity-70">{employees.filter((e)=>e.position===p).length}</span></button>
          ))}
        </div>
      </div>

      <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="emp-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Employee</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-2">Position</div>
          <div className="col-span-2">Pay</div>
          <div className="col-span-2 text-right">Contact</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {filtered.length === 0 && <div className="py-8 ink-subtle text-center text-sm">No employees match the filter.</div>}
        {filtered.map((e) => (
          <button key={e.id} onClick={() => openEdit(e)} data-testid={`emp-row-${e.id}`}
                  className="w-full text-left grid grid-cols-12 py-3 items-center hover:bg-foreground/[0.03] transition-colors">
            <div className="col-span-3 pr-2 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] uppercase tracking-widest font-bold shrink-0">
                {e.name.split(" ").map((n)=>n[0]).slice(0,2).join("")}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{e.name}</div>
                <div className="text-xs ink-subtle truncate">{e.notes || "—"}</div>
              </div>
            </div>
            <div className="col-span-2 mono text-sm ink">{e.code}</div>
            <div className="col-span-2 tiny-label">{e.position}</div>
            <div className="col-span-2 mono text-sm ink">
              {e.pay_type === "salary" ? `${peso(e.pay_rate)} /mo`
                : e.pay_type === "per_shift" ? `${peso(e.pay_rate)} /shift`
                : `${peso(e.pay_rate)} /hr`}
            </div>
            <div className="col-span-2 text-right text-xs ink-subtle truncate">{e.contact_phone || e.contact_email || "—"}</div>
            <div className="col-span-1 text-right">
              {e.active
                ? <span className="text-[9px] uppercase tracking-[0.22em] font-bold ink">ACTIVE</span>
                : <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-red-500">OFF</span>}
            </div>
          </button>
        ))}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="emp-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setCreating(false); setEditing(null); }}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">HR · Roster</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing ? "Edit employee" : "Add employee"}</h2>
              </div>
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Full name"><input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="emp-form-name"/></F>
            <F label="Badge code (unique)"><input required disabled={!!editing} value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} className="i mono" data-testid="emp-form-code" placeholder="EMP011"/></F>

            <div className="grid grid-cols-2 gap-3">
              <F label="Position">
                <select required value={form.position} onChange={(e)=>setForm({...form, position: e.target.value})} className="i" data-testid="emp-form-position">
                  {POSITIONS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </F>
              <F label="Pay type">
                <select required value={form.pay_type} onChange={(e)=>setForm({...form, pay_type: e.target.value})} className="i" data-testid="emp-form-pay-type">
                  {PAY_TYPES.map((p) => <option key={p} value={p}>{p.replace("_"," ")}</option>)}
                </select>
              </F>
            </div>

            <F label={`Rate (₱ per ${form.pay_type === "salary" ? "month" : form.pay_type === "per_shift" ? "shift" : "hour"})`}>
              <input required type="number" step="0.01" value={form.pay_rate} onChange={(e)=>setForm({...form, pay_rate: e.target.value})} className="i mono" data-testid="emp-form-rate"/>
            </F>

            <div className="grid grid-cols-2 gap-3">
              <F label="Email"><input type="email" value={form.contact_email} onChange={(e)=>setForm({...form, contact_email: e.target.value})} className="i" data-testid="emp-form-email"/></F>
              <F label="Phone"><input value={form.contact_phone} onChange={(e)=>setForm({...form, contact_phone: e.target.value})} className="i" data-testid="emp-form-phone"/></F>
            </div>

            <F label="Notes"><textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i-tall"/></F>

            <div className="mt-6 border-t hairline-soft pt-4">
              <div className="section-eyebrow mb-2">Bank details · for payroll CSV</div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Bank name"><input value={form.bank_name || ""} onChange={(e)=>setForm({...form, bank_name: e.target.value})} placeholder="BPI / BDO / Metrobank" className="i" data-testid="emp-form-bank-name"/></F>
                <F label="Account number"><input value={form.bank_account_number || ""} onChange={(e)=>setForm({...form, bank_account_number: e.target.value})} placeholder="10-digit account" className="i mono" data-testid="emp-form-bank-account"/></F>
              </div>
              <F label="Account name (if different from employee)"><input value={form.bank_account_name || ""} onChange={(e)=>setForm({...form, bank_account_name: e.target.value})} placeholder="Defaults to employee name" className="i" data-testid="emp-form-bank-account-name"/></F>
            </div>

            {editing && (
              <div className="mt-6">
                <button type="button" onClick={() => { toggle(editing); setCreating(false); setEditing(null); }}
                        className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-500 hover:underline">
                  {editing.active ? "Deactivate" : "Reactivate"}
                </button>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="emp-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Users size={13}/> {editing ? "Save" : "Add"}</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }
                     .i:disabled { opacity: 0.5; }
                     .i-tall { margin-top: 6px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 10px 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { code: "", name: "", position: "bartender", pay_type: "hourly", pay_rate: 150, contact_email: "", contact_phone: "", notes: "", bank_name: "", bank_account_number: "", bank_account_name: "" };

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
